import React from 'react';
import { Heart, ArrowRight, HandCoins, Shield, Star, Trophy, Mail, Phone, CheckCircle2 } from 'lucide-react';
import { contactEmails, iceLogo, heroImages } from '../mock';
import { useToast } from '../hooks/use-toast';

const tiers = [
  {
    name: 'PATRIOT',
    price: '$250',
    color: 'border-[#0a1f44]',
    accent: 'bg-[#0a1f44]',
    perks: [
      'Name listed on the Patriots website sponsor wall',
      'Featured on the BJP Patriots Facebook page',
      'Social media thank-you post'
    ]
  },
  {
    name: 'CHAMPION',
    price: '$1,000',
    color: 'border-[#c8102e]',
    accent: 'bg-[#c8102e]',
    featured: true,
    perks: [
      'Everything in PATRIOT tier',
      'Logo on team practice banner',
      'Recognition at Homecoming & Alumni Day',
      'Two Patriots team T-shirts',
      'Shout-out during Season Kickoff BBQ'
    ]
  },
  {
    name: 'LEGACY',
    price: '$5,000+',
    color: 'border-[#0a1f44]',
    accent: 'bg-[#0a1f44]',
    perks: [
      'Everything in CHAMPION tier',
      'Logo on field banner',
      'Sponsor spotlight on home page',
      'Custom naming opportunity (equipment, scholarship, or event)'
    ]
  }
];

const impact = [
  { icon: Shield, title: 'Safe Equipment', text: 'Helmets, pads, and Riddell-certified gear for every scholar.' },
  { icon: Trophy, title: 'Tournament Travel', text: 'Buses, hotels, and entry fees for regional & national play.' },
  { icon: Star, title: 'Scholarships', text: 'Registration & fees waived for families who need it most.' },
  { icon: HandCoins, title: 'Field & Facility', text: 'Turf time, practice lights, and cheer gym rentals.' }
];

export default function Sponsors() {
  const { toast } = useToast();

  const handleDonate = () => {
    window.open('https://app.autobooks.co/pay/brookline-jamaica-plain', '_blank');
  };

  const handleSponsorInquiry = () => {
    toast({
      title: 'Thanks for your interest!',
      description: `Email ${contactEmails.join(' or ')} or call 617-818-8296 to complete your sponsorship.`
    });
  };

  return (
    <div className="font-body">
      {/* Header */}
      <section className="relative pt-40 pb-24 bg-[#0a1f44] overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImages[1]} alt="stadium" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1f44]/70 to-[#0a1f44]" />
          <div className="absolute inset-0 diagonal-stripes opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <img src={iceLogo} alt="ICE Logo" className="h-14 w-auto object-contain" data-testid="sponsors-hero-logo" />
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs">Support the Patriots</div>
          </div>
          <h1 className="font-display text-white text-6xl md:text-8xl leading-none" data-testid="sponsors-title">
            SPONSORS <span className="text-[#c8102e]">&</span> DONATE
          </h1>
          <p className="text-white/75 max-w-2xl mt-6 text-lg">
            Every dollar keeps a scholar on the field. Partner with the &quot;ICE&quot; Brookline JP Patriots to fuel youth football, cheer, and community for another 20 years.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button onClick={handleDonate} data-testid="sponsors-donate-btn" className="group inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest px-8 py-4 transition-colors">
              <Heart className="w-4 h-4" /> Donate Now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a href="#tiers" data-testid="sponsors-see-tiers-btn" className="inline-flex items-center gap-2 border-2 border-white/70 hover:border-white text-white font-heading uppercase tracking-widest px-8 py-4 transition-colors hover:bg-white hover:text-[#0a1f44]">
              See Sponsor Tiers
            </a>
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Where your dollars go</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">REAL IMPACT</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {impact.map((it) => (
              <div key={it.title} data-testid={`impact-${it.title.toLowerCase().replace(/\s+/g, '-')}`} className="hover-lift border-2 border-[#0a1f44]/10 p-8">
                <div className="w-12 h-12 bg-[#c8102e] text-white flex items-center justify-center mb-5">
                  <it.icon className="w-6 h-6" />
                </div>
                <div className="font-heading uppercase tracking-widest text-[#0a1f44] font-semibold mb-2">{it.title}</div>
                <p className="text-[#0a1f44]/70 text-sm leading-relaxed">{it.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Legacy Sponsors */}
      <section className="py-20 bg-white border-t border-[#0a1f44]/10" data-testid="current-legacy-sponsors">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Champions Behind the Program</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">LEGACY SPONSORS</h2>
            <p className="text-[#0a1f44]/70 mt-4">The families and businesses making the &quot;ICE&quot; Brookline JP Patriots possible. Thank you.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <div data-testid="legacy-sponsor-madstorm" className="hover-lift bg-gradient-to-br from-[#0a1f44] to-[#152d5a] text-white p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 border-t-4 border-[#c8102e] shadow-xl">
              <div className="flex-shrink-0 bg-white p-4">
                <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_navy-red-hub/artifacts/xg3dkld7_crest%20color.webp" alt="Madstorm Production LLC crest" className="w-40 sm:w-48 h-auto object-contain" data-testid="legacy-sponsor-madstorm-logo" loading="lazy" decoding="async" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 bg-[#c8102e] text-white font-heading uppercase tracking-[0.3em] text-[10px] px-3 py-1.5 mb-4">
                  <Star className="w-3 h-3 fill-white" /> Legacy Tier
                </div>
                <h3 className="font-display text-4xl md:text-5xl leading-tight mb-3">MADSTORM<br />PRODUCTION LLC</h3>
                <p className="text-white/75 leading-relaxed">Proud Legacy sponsor of the &quot;ICE&quot; Brookline Jamaica Plain Patriots Pop Warner Football & Cheerleading. Powering scholars, championships, and community for the next 20 years.</p>
              </div>
            </div>

            <div data-testid="sponsor-shamar-martin" className="hover-lift mt-8 bg-white text-[#0a1f44] border border-[#0a1f44]/10 border-t-4 border-t-[#0a1f44] shadow-xl grid md:grid-cols-12">
              <div className="md:col-span-5 relative bg-[#0a1f44]">
                <img src="/sponsors/sgt-shamar-martin.jpg" alt="Sgt. Shamar Martin in uniform standing beside a United States Army Chinook helicopter" className="w-full h-full max-h-[520px] object-cover object-right" data-testid="sponsor-shamar-martin-photo" loading="lazy" decoding="async" />
                <span className="absolute left-0 top-4 bg-[#c8102e] text-white font-heading uppercase tracking-[0.3em] text-[10px] px-3 py-1.5">U13 Team Sponsor</span>
              </div>
              <div className="md:col-span-7 p-8 md:p-10 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 self-start bg-[#0a1f44] text-white font-heading uppercase tracking-[0.3em] text-[10px] px-3 py-1.5 mb-4">
                  <Shield className="w-3 h-3" /> Thank You · U.S. Army
                </div>
                <h3 className="font-display text-4xl md:text-5xl leading-tight mb-3">SGT. SHAMAR<br />MARTIN</h3>
                <p className="text-[#0a1f44]/80 leading-relaxed" data-testid="sponsor-shamar-martin-message">Thank you, Sgt. Shamar Martin, for your generous donation to the &quot;ICE&quot; Brookline Jamaica Plain Pop Warner Football &amp; Cheerleading <strong>U13 team</strong>. Thank you for joining and becoming a part of the BJP Patriots family — and thank you for your service to this nation.</p>
                <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e] mt-5">— Coaches, players &amp; families of the BJP Patriots</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section id="tiers" className="py-24 bg-[#f5f6f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Sponsorship Tiers</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">JOIN THE FAMILY</h2>
            <p className="text-[#0a1f44]/70 mt-4">Businesses, alumni, and community partners — pick the tier that fits and we&apos;ll take it from there.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((t) => (
              <div key={t.name} data-testid={`tier-${t.name.toLowerCase()}`} className={`hover-lift bg-white p-8 border-t-8 ${t.color} ${t.featured ? 'md:-translate-y-4 shadow-2xl' : 'shadow-sm'}`}>
                {t.featured && (
                  <div className="inline-block bg-[#c8102e] text-white text-xs font-heading uppercase tracking-widest px-3 py-1 mb-4">Most Popular</div>
                )}
                <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-2">{t.name}</div>
                <div className="font-display text-5xl text-[#0a1f44] mb-6">{t.price}</div>
                <ul className="space-y-3 mb-8">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-[#0a1f44]/80 text-sm">
                      <CheckCircle2 className="w-5 h-5 text-[#c8102e] flex-shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={handleSponsorInquiry} data-testid={`tier-${t.name.toLowerCase()}-btn`} className={`w-full ${t.accent} hover:opacity-90 text-white font-heading uppercase tracking-widest text-sm px-6 py-3 transition-opacity`}>
                  Become a {t.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0a1f44] relative overflow-hidden">
        <div className="absolute inset-0 stars-bg" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Heart className="w-10 h-10 text-[#c8102e] mx-auto mb-6" />
          <h2 className="font-display text-white text-5xl md:text-6xl mb-6">EVERY DOLLAR COUNTS</h2>
          <p className="text-white/80 text-lg mb-8 leading-relaxed">
            Prefer to give a one-time donation? Every contribution goes directly to equipment, tournament fees, and scholarships for our young scholars.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={handleDonate} data-testid="sponsors-donate-cta" className="group inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest px-10 py-4 transition-colors">
              <Heart className="w-4 h-4" /> Donate Now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="mt-12 pt-12 border-t border-white/10 grid sm:grid-cols-2 gap-6 text-left max-w-2xl mx-auto">
            <div data-testid="sponsors-email-list" className="flex items-start gap-4 text-white">
              <Mail className="w-5 h-5 text-[#c8102e]" />
              <div className="min-w-0">
                <div className="font-heading uppercase tracking-widest text-xs text-white/60">Sponsor Inquiries</div>
                {contactEmails.map((email, index) => (
                  <a key={email} href={`mailto:${email}`} data-testid={`sponsors-email-${index + 1}`} className="block font-heading break-all hover:text-[#c8102e] transition-colors">{email}</a>
                ))}
              </div>
            </div>
            <a href="tel:6178188296" data-testid="sponsors-phone" className="flex items-center gap-4 text-white hover:text-[#c8102e] transition-colors">
              <Phone className="w-5 h-5 text-[#c8102e]" />
              <div>
                <div className="font-heading uppercase tracking-widest text-xs text-white/60">Call the Program</div>
                <div className="font-heading">617-818-8296</div>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
